import React, { FC, ReactNode } from 'react'

import { Button, useIsTabletOrMobile } from '@avantstay/backoffice-core'

import { FiltersProps } from '@new/__global/components/Filters/_types'
import { FiltersWidget } from '@new/__global/components/Filters/FiltersWidget'
import { IconRemoveCircle } from '@avantstay/backoffice-vectors'

import * as S from './Filters.styles'

const createFilterInternalItem = (component: ReactNode, index: number) => {
  return component ? (
    <S.FilterInternalItem key={index.toString()}>{component}</S.FilterInternalItem>
  ) : null
}

const visibleComponentFilter = (maxDisplay: number, isMobile: boolean, components: ReactNode[]) => {
  return components.filter((_value, index) => isMobileChange(index, maxDisplay, isMobile))
}

const filterInternalItemMapper = (components: ReactNode[]) => {
  return components.slice(1)?.map((component, index) => createFilterInternalItem(component, index))
}

const isMobileChange = (index: number, maxDisplay: number, isMobile: boolean) => {
  return isMobile ? index === 0 : index <= maxDisplay - 1
}

const Filters: FC<FiltersProps> = ({
  appliedFiltersComponent,
  appliedFiltersCounter,
  className,
  clearAllDataCy,
  elementOnTheRight,
  hasStickyElementOnTheRight = false,
  appliedFiltersElementOnTheRight,
  filters,
  isClearAllDisabled = false,
  isSidebarOpen,
  maxFilterDisplay = 3,
  onApplyFilters,
  onClearAllFilters,
  onClearOtherFilters,
  otherFilters,
  secondRow,
  background,
}) => {
  const isTabletOrMobile = useIsTabletOrMobile()

  const validComponents = filters.filter(component => !!component)

  const visibleComponents = visibleComponentFilter(
    maxFilterDisplay,
    isTabletOrMobile,
    validComponents,
  )

  const filterInternalItems = () => filterInternalItemMapper(validComponents)

  return (
    <S.Root className={className} $isSidebarOpen={isSidebarOpen} background={background}>
      <S.Filters>
        <S.Row>
          {!isTabletOrMobile && otherFilters ? otherFilters : null}

          <S.Wrapper>
            <S.Container>
              {visibleComponents.map((value, index) =>
                value ? (
                  <S.FilterItem key={`filter-item-${index.toString()}`}>{value}</S.FilterItem>
                ) : null,
              )}

              {isTabletOrMobile && validComponents.length > 1 ? (
                <FiltersWidget
                  countSelectedFilters={appliedFiltersCounter}
                  isClearAllDisabled={isClearAllDisabled}
                  onApplyFilters={onApplyFilters}
                  onClearAllFilters={onClearAllFilters}
                  onClearOtherFilters={onClearOtherFilters}
                >
                  {filterInternalItems()}
                  {otherFilters}
                </FiltersWidget>
              ) : null}
            </S.Container>
          </S.Wrapper>

          {!isTabletOrMobile && onClearAllFilters ? (
            <S.ButtonContainer>
              <Button.Flat
                data-cy={clearAllDataCy}
                disabled={isClearAllDisabled}
                onClick={onClearAllFilters}
              >
                <IconRemoveCircle /> &nbsp; Clear all
              </Button.Flat>
            </S.ButtonContainer>
          ) : null}

          {elementOnTheRight ? (
            <S.RightSide $isSticky={hasStickyElementOnTheRight}>{elementOnTheRight}</S.RightSide>
          ) : null}
        </S.Row>

        {appliedFiltersComponent || appliedFiltersElementOnTheRight ? (
          <S.Row>
            {!isTabletOrMobile && appliedFiltersComponent ? (
              <S.AppliedFiltersRow>{appliedFiltersComponent}</S.AppliedFiltersRow>
            ) : null}
            {appliedFiltersElementOnTheRight ? (
              <S.RightSide $isSticky={hasStickyElementOnTheRight}>
                {appliedFiltersElementOnTheRight}
              </S.RightSide>
            ) : null}
          </S.Row>
        ) : null}

        {secondRow ? <S.Row>{secondRow}</S.Row> : null}
      </S.Filters>
    </S.Root>
  )
}

export default Filters
